import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import useApi from '../../api';
import toastConfig from '../../config/Toast';
import { TaskDefinition } from './TaskDefinitionEditor';

interface TaskDefinitionCreateDialogProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (taskDefinition: TaskDefinition) => void;
}

function taskNameToSystemName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '');
}

export default function TaskDefinitionCreateDialog({
  agentId,
  open,
  onOpenChange,
  onCreated,
}: TaskDefinitionCreateDialogProps) {
  const { post } = useApi();
  const [name, setName] = useState('');
  const [systemName, setSystemName] = useState('');
  const [instruction, setInstruction] = useState('');
  const [systemNameEdited, setSystemNameEdited] = useState(false);
  const [creating, setCreating] = useState(false);

  const resetForm = () => {
    setName('');
    setSystemName('');
    setInstruction('');
    setSystemNameEdited(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetForm();
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!systemNameEdited) {
      setSystemName(taskNameToSystemName(value));
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await post(`/agents/${agentId}/task-definitions/create`, {
        name: name.trim(),
        system_name: systemName.trim(),
        instruction: instruction.trim(),
      });
      onCreated(response.data);
      toast.success('Task definition created', toastConfig);
      handleOpenChange(false);
    } catch (error: any) {
      console.error('Error creating task definition:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create task definition', toastConfig);
    } finally {
      setCreating(false);
    }
  };

  const canCreate = Boolean(name.trim() && systemName.trim() && instruction.trim()) && !creating;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Task Definition</DialogTitle>
          <DialogDescription>
            Write reusable step-by-step instructions for this agent to follow in app mode.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-td-name">Name</Label>
            <Input
              id="create-td-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Create New Project"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-td-system-name">System Name</Label>
            <Input
              id="create-td-system-name"
              value={systemName}
              onChange={(e) => {
                setSystemNameEdited(true);
                setSystemName(e.target.value);
              }}
              placeholder="e.g. create_new_project"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Use snake_case. The agent uses this value when loading a specific task.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-td-instruction">Instructions</Label>
            <Textarea
              id="create-td-instruction"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              placeholder={'1. Navigate to the Projects page\n2. Click Create Project\n3. Enter [project name]'}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate}>
            {creating ? 'Creating...' : 'Create Task Definition'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
